import React from 'react';
import {
  ReserveConfirmOutlineBox,
  ReserveConfirmOutlineBoxTitle
} from '@this/components/reserve_trip/reserve_confirm/outline/shared';
import type Organization from '@this/domain/organization/organization2';
import Transit from '@this/domain/transit/transit';
import Hotel from '@this/domain/hotel/hotel';
import type ReservingTrip from '@this/domain/trip/reserving_trip';
import type ReservingTripItem from '@this/domain/trip/reserving_trip_item';

type BufferAmountsSectionProps = {
  reservingTrip: ReservingTrip;
  organization: Organization;
};

type BufferItemData = {
  key: string;
  title: string;
  bufferAmountText: string;
};

const styles = {
  item: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '5px'
  },
  container: {
    padding: '10px',
    fontWeight: 'bold' as const,
    color: '#959595'
  }
};

const BufferAmountsSection: React.FC<BufferAmountsSectionProps> = ({ reservingTrip, organization }) => {
  const createTransitBufferItem = (item: ReservingTripItem, index: number) => {
    if (
      !(item.element instanceof Transit) ||
      !item.element.airExists() ||
      item.totalBufferAmount(organization) <= 0
    ) {
      return null;
    }

    return {
      key: `transit-${index}`,
      title: '国内航空券',
      bufferAmountText: item.element.bufferAmountText(organization)
    };
  };

  const createHotelBufferItem = (item: ReservingTripItem, index: number) => {
    if (!(item.element instanceof Hotel) || item.totalBufferAmount(organization) <= 0) {
      return null;
    }

    const hotelType = item.element.isForeign() ? '海外ホテル' : '国内ホテル';
    const title = `${hotelType}${item.isHotelPriceExceeded() ? ' (規定料金超過)' : ''}`;

    return {
      key: `hotel-${index}`,
      title,
      bufferAmountText: item.element.bufferAmountText(
        organization,
        item.isHotelPriceExceeded(),
        item.hotelPriceLimit,
        item.element.getAveragePriceWithTax() || 0
      )
    };
  };

  // 表示用データを事前に整形
  const bufferItems = reservingTrip.items
    .filter(item => item !== undefined)
    .flatMap((item, index) => {
      const results: BufferItemData[] = [];

      const transitItem = createTransitBufferItem(item, index);
      if (transitItem) results.push(transitItem);

      const hotelItem = createHotelBufferItem(item, index);
      if (hotelItem) results.push(hotelItem);

      return results;
    });

  return (
    <ReserveConfirmOutlineBox>
      <ReserveConfirmOutlineBoxTitle>各商品の許容幅</ReserveConfirmOutlineBoxTitle>
      <div style={styles.container}>
        {reservingTrip.isPackage() ? (
          <div style={styles.item}>
            <p>パッケージ</p>
            <p>{`${utils.digits(organization.getPackageBufferAmount())}円 * ${reservingTrip.peopleNum}名`}</p>
          </div>
        ) : (
          bufferItems.map(item => (
            <div key={item.key} style={styles.item}>
              <p>{item.title}</p>
              <p>{item.bufferAmountText}</p>
            </div>
          ))
        )}
      </div>
    </ReserveConfirmOutlineBox>
  );
};

export default BufferAmountsSection;
